import React, { forwardRef } from 'react';

export type SelectSize = 'small' | 'medium' | 'large';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Options for the select */
  options: SelectOption[];
  /** Label for the select */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error state or message */
  error?: boolean | string;
  /** Loading state */
  loading?: boolean;
  /** Size variant */
  size?: SelectSize;
  /** Full width flag */
  fullWidth?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Container className */
  containerClassName?: string;
  /** Label className */
  labelClassName?: string;
  /** Helper text className */
  helperTextClassName?: string;
}

/**
 * Select component for dropdown selection
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      label,
      helperText,
      error = false,
      loading = false,
      size = 'medium',
      fullWidth = false,
      placeholder,
      disabled = false,
      containerClassName = '',
      labelClassName = '',
      helperTextClassName = '',
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    // Generate unique ID for accessibility
    const selectId = id || `select-${Math.random().toString(36).substring(2, 9)}`;
    
    // Size classes
    const sizeClasses = {
      small: 'py-1 px-2 text-sm',
      medium: 'py-2 px-3',
      large: 'py-3 px-4 text-lg',
    };
    
    // Full width class
    const widthClass = fullWidth ? 'w-full' : '';
    
    // Base classes
    const baseClasses = 'bg-white dark:bg-gray-700 border rounded-md focus:outline-none focus:ring-1';
    
    // State classes
    const stateClasses = error
      ? 'border-error focus:border-error focus:ring-error'
      : 'border-gray-300 dark:border-gray-600 focus:border-primary focus:ring-primary';
    
    // Disabled/loading classes
    const disabledClasses = (disabled || loading) 
      ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800' 
      : '';
    
    // Select classes
    const selectClasses = [
      baseClasses,
      sizeClasses[size],
      stateClasses,
      disabledClasses,
      widthClass,
      'appearance-none',
      'pr-8', // Space for the dropdown icon
      className,
    ].join(' ');

    return (
      <div className={`flex flex-col ${widthClass} ${containerClassName}`}>
        {label && (
          <label 
            htmlFor={selectId} 
            className={`mb-1 text-sm font-medium text-gray-700 dark:text-gray-300 ${labelClassName}`}
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled || loading}
            className={selectClasses}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option 
                key={option.value} 
                value={option.value} 
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading ? (
              <svg 
                className="animate-spin h-4 w-4 text-gray-500" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg 
                className="h-5 w-5 text-gray-400" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path 
                  fillRule="evenodd" 
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                  clipRule="evenodd" 
                />
              </svg>
            )}
          </div>
        </div>
        
        {(helperText || error) && (
          <p 
            className={`mt-1 text-sm ${
              error 
                ? 'text-error' 
                : 'text-gray-500 dark:text-gray-400'
            } ${helperTextClassName}`}
          >
            {typeof error === 'string' ? error : helperText}
          </p>
        )}
      </div>
    );
  }
);

// Display name for debugging
Select.displayName = 'Select';

export default Select; 